import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getFirestore, doc, updateDoc, getDoc } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { useNotification } from "../common/NotificationContext";
import { InputFieldParticipants } from "../common/InputFieldParticipants";

export const ParticipantsEdit = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const db = getFirestore();
  const auth = getAuth();

  const [formData, setFormData] = useState({
    namaDepan: state?.participant?.namaLengkap?.split(" ")[0] || "",
    namaBelakang:
      state?.participant?.namaLengkap?.split(" ").slice(1).join(" ") || "",
    namaLengkap: state?.participant?.namaLengkap || "",
    email: state?.participant?.email || "",
    alamat: state?.participant?.alamat || "",
    bibName: state?.participant?.bibName || "",
    bibNumber: state?.participant?.bibNumber || "",
    gender: state?.participant?.gender || "",
    golonganDarah: state?.participant?.golonganDarah || "",
    hubunganKontakDarurat: state?.participant?.hubunganKontakDarurat || "",
    kontakDarurat: state?.participant?.kontakDarurat || "",
    kota: state?.participant?.kota || "",
    namaKontakDarurat: state?.participant?.namaKontakDarurat || "",
    negara: state?.participant?.negara || "Indonesia",
    nomorIdentitas: state?.participant?.nomorIdentitas || "",
    provinsi: state?.participant?.provinsi || "",
    sizeJersey: state?.participant?.sizeJersey || "",
    tanggalLahir: state?.participant?.tanggalLahir || "",
    telepon: state?.participant?.telepon || "",
    kategori: state?.participant?.kategori || "",
    orderId: state?.participant?.orderId || "",
    transaction_status: state?.participant?.transaction_status || "",
    harga: state?.participant?.harga || 0,
    distance: state?.participant?.distance || 0,
    performanceIndex: state?.participant?.performanceIndex || "",
    stravaProfile: state?.participant?.stravaProfile || "",
    errors: {}, // Tambahkan errors ke formData
  });
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [eventId] = useState("ULTRA24");

  // Cek status admin
  useEffect(() => {
    const checkAdminStatus = async () => {
      const user = auth.currentUser;
      if (user) {
        try {
          const idTokenResult = await user.getIdTokenResult();
          const isAdminUser = idTokenResult.claims.role === "admin";
          console.log("[DEBUG] Admin status in ParticipantsEdit:", isAdminUser);
          setIsAdmin(isAdminUser);
        } catch (err) {
          console.error("[ERROR] Error checking admin status:", err);
          setIsAdmin(false);
        }
      }
    };
    checkAdminStatus();
  }, [auth]);

  // Fetch categories from semuaKegiatan
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const eventRef = doc(db, "semuaKegiatan", eventId);
        const eventDoc = await getDoc(eventRef);
        if (eventDoc.exists()) {
          const eventData = eventDoc.data();
          const categoriesData = eventData.categories || [];
          console.log(
            "[DEBUG] Fetched categories in ParticipantsEdit:",
            categoriesData
          );
          setCategories(categoriesData); // Simpan semua kategori, termasuk yang ditutup
        } else {
          console.error(
            "[ERROR] Event document not found for eventId:",
            eventId
          );
          showNotification("error", "Data kegiatan tidak ditemukan.");
        }
      } catch (err) {
        console.error("[ERROR] Error fetching categories:", err);
        showNotification("error", "Gagal memuat kategori. Silakan coba lagi.");
      }
    };
    fetchCategories();
  }, [db, eventId, showNotification]);

  const handleChange = (e, participantIndex = null) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const updated = { ...prev, [name]: value };

      // Update namaLengkap when namaDepan or namaBelakang changes
      if (name === "namaDepan" || name === "namaBelakang") {
        updated.namaLengkap = `${updated.namaDepan || ""} ${
          updated.namaBelakang || ""
        }`.trim();
      }
      // Update namaDepan and namaBelakang when namaLengkap changes
      if (name === "namaLengkap") {
        const namaSplit = value.trim().split(/\s+/);
        updated.namaDepan = namaSplit[0] || "";
        updated.namaBelakang = namaSplit.slice(1).join(" ") || "";
      }
      // Update harga and distance when kategori changes
      if (name === "kategori") {
        const selectedCategory = categories.find(
          (cat) => cat.categoryName === value
        );
        if (selectedCategory) {
          updated.distance = selectedCategory.distance || 0;
          updated.harga =
            prev.negara === "Indonesia"
              ? selectedCategory.price
              : selectedCategory.foreignerPrice;
        }
        // Hapus error kategori untuk admin
        if (isAdmin) {
          updated.errors = { ...updated.errors, kategori: undefined };
          console.log("[DEBUG] Cleared kategori error for admin:", {
            kategori: value,
          });
        }
      }
      // Update errors jika disediakan
      if (name === "errors") {
        updated.errors = value;
      }

      console.log("[DEBUG] Updated formData:", updated);
      return updated;
    });
  };

  const humanize = (str) => {
    if (!str) return "";
    if (str === "nomorIdentitas") return "Nomor Identitas";
    if (str === "bibNumber") return "BIB Number";
    const withSpaces = str.replace(/([A-Z])/g, " $1").trim();
    return withSpaces
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const validateFormat = (field, value, negara = "Indonesia") => {
    const label = humanize(field);
    console.log("[DEBUG] Validating field:", { field, value, negara, isAdmin });

    // Allow empty bibNumber
    if (
      field === "bibNumber" &&
      (value === undefined || value === null || value === "")
    ) {
      return "";
    }

    // Validate required fields
    if (field !== "bibNumber" && (!value || value.trim() === "")) {
      return `${label} wajib diisi`;
    }

    if (field === "namaDepan" || field === "namaBelakang") {
      if (!/^[A-Za-z\s]{1,50}$/.test(value)) {
        return `${label} hanya boleh huruf dan spasi, maksimum 50 karakter.`;
      }
    }
    if (field === "email" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      return `${label} harus dalam format yang valid (contoh: user@example.com).`;
    }
    if (field === "bibName" && !/^[A-Za-z0-9\s]{1,20}$/.test(value)) {
      return `${label} harus 1-20 karakter alfanumerik dan spasi.`;
    }
    if (field === "bibNumber" && value) {
      if (!/^\d{1,4}$/.test(value)) {
        return `${label} harus 1-4 digit angka, misalnya: "1234"`;
      }
    }
    if (field === "telepon" || field === "kontakDarurat") {
      if (!/^\+?\d{8,15}$/.test(value)) {
        return `${label} harus 8-15 digit, boleh diawali dengan "+".`;
      }
    }
    if (field === "nomorIdentitas") {
      if (negara === "Indonesia") {
        if (!/^\d{16}$/.test(value)) {
          return `${label} (KTP) harus 16 digit untuk peserta dari Indonesia.`;
        }
      } else {
        if (!/^[A-Za-z0-9]{5,20}$/.test(value)) {
          return `${label} (Paspor/Identitas) harus 5-20 karakter alfanumerik untuk peserta asing.`;
        }
      }
    }
    if (field === "tanggalLahir") {
      const date = new Date(value);
      const eventDate = new Date("2025-09-27");
      const minAgeDate = new Date(
        eventDate.getFullYear() - 15,
        eventDate.getMonth(),
        eventDate.getDate()
      );
      if (isNaN(date.getTime()) || date >= eventDate) {
        return `${label} harus di masa lalu sebelum 27/9/2025, misalnya: "2010-09-27"`;
      } else if (date > minAgeDate) {
        return `Peserta harus berusia minimal 15 tahun pada 27/9/2025, misalnya: "2010-09-27" atau sebelumnya`;
      }
    }
    if (field === "golonganDarah" && !["A", "B", "AB", "O"].includes(value)) {
      return `${label} harus A, B, AB, atau O.`;
    }
    if (
      field === "sizeJersey" &&
      !["S", "M", "L", "XL", "XXL"].includes(value)
    ) {
      return `${label} harus S, M, L, XL, atau XXL.`;
    }
    if (field === "gender" && !["Laki-laki", "Perempuan"].includes(value)) {
      return `${label} harus Laki-laki atau Perempuan.`;
    }
    if (field === "alamat" && !/^[A-Za-z0-9\s,.-/#()]{5,100}$/.test(value)) {
      return `${label} harus 5-100 karakter, boleh huruf, angka, spasi, koma, titik, tanda hubung, garis miring, pagar, atau tanda kurung.`;
    }
    if (field === "namaKontakDarurat" && !/^[A-Za-z\s]{1,50}$/.test(value)) {
      return `${label} hanya boleh huruf dan spasi, maksimum 50 karakter.`;
    }
    if (
      field === "hubunganKontakDarurat" &&
      !/^[A-Za-z\s]{1,50}$/.test(value)
    ) {
      return `${label} hanya boleh huruf dan spasi, maksimum 50 karakter.`;
    }
    if (field === "performanceIndex" && !/^\d{3}$/.test(value)) {
      return `${label} harus berupa 3 digit angka, isi "000" jika tidak ada index`;
    }
    if (field === "stravaProfile" && !value.trim()) {
      return `${label} atau aplikasi sejenis wajib diisi`;
    }
    if (field === "kategori") {
      console.log("[DEBUG] Validating kategori:", {
        value,
        categories: categories.map((cat) => cat.categoryName),
        isAdmin,
      });
      if (isAdmin) {
        console.log("[DEBUG] Bypassing kategori validation for admin:", {
          value,
        });
        return "";
      }
      if (!categories.some((cat) => cat.categoryName === value)) {
        return `${label} tidak valid atau pendaftaran untuk kategori ini ditutup.`;
      }
      const selectedCategory = categories.find(
        (cat) => cat.categoryName === value
      );
      if (selectedCategory && !selectedCategory.isRegistrationOpen) {
        return `${label} pendaftaran untuk ${value} telah ditutup.`;
      }
    }
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Validation
    const requiredFields = [
      "namaDepan",
      "namaBelakang",
      "email",
      "telepon",
      "nomorIdentitas",
      "tanggalLahir",
      "gender",
      "negara",
      "provinsi",
      "kota",
      "alamat",
      "kontakDarurat",
      "namaKontakDarurat",
      "hubunganKontakDarurat",
      "bibName",
      "golonganDarah",
      "sizeJersey",
      "performanceIndex",
      "stravaProfile",
      "kategori",
    ];

    const errors = {};
    for (const field of requiredFields) {
      const error = validateFormat(field, formData[field], formData.negara);
      if (error) {
        errors[field] = error;
      }
    }

    // Validate bibNumber separately (optional)
    if (formData.bibNumber && validateFormat("bibNumber", formData.bibNumber)) {
      errors.bibNumber = validateFormat("bibNumber", formData.bibNumber);
    }

    if (Object.keys(errors).length > 0) {
      const firstError = Object.values(errors)[0];
      console.log("[DEBUG] Validation errors on submit:", errors);
      showNotification("error", firstError);
      setFormData((prev) => ({ ...prev, errors }));
      setLoading(false);
      return;
    }

    try {
      const participantRef = doc(db, "pendaftaranJOTR", state?.participant?.id);
      await updateDoc(participantRef, {
        namaDepan: formData.namaDepan,
        namaBelakang: formData.namaBelakang,
        namaLengkap: formData.namaLengkap,
        email: formData.email,
        telepon: formData.telepon,
        nomorIdentitas: formData.nomorIdentitas,
        tanggalLahir: formData.tanggalLahir,
        gender: formData.gender,
        negara: formData.negara,
        provinsi: formData.provinsi,
        kota: formData.kota,
        alamat: formData.alamat,
        kontakDarurat: formData.kontakDarurat,
        namaKontakDarurat: formData.namaKontakDarurat,
        hubunganKontakDarurat: formData.hubunganKontakDarurat,
        bibName: formData.bibName,
        bibNumber: formData.bibNumber || "",
        golonganDarah: formData.golonganDarah,
        sizeJersey: formData.sizeJersey,
        performanceIndex: formData.performanceIndex,
        stravaProfile: formData.stravaProfile,
        kategori: formData.kategori,
        orderId: formData.orderId,
        transaction_status: formData.transaction_status,
        harga: formData.harga,
        distance: formData.distance,
        updatedAt: new Date(),
      });

      showNotification("success", "Data peserta berhasil diperbarui.");
      navigate("/participants");
    } catch (err) {
      console.error("[ERROR] Error updating participant:", err);
      showNotification(
        "error",
        "Gagal memperbarui data peserta. Silakan coba lagi."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/participants");
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white shadow-xl px-4 sm:px-6 lg:px-8 z-0">
      <div className="max-w-7xl mx-auto flex flex-col md:flex-row gap-4 py-20 overflow-visible">
        <div className="w-full md:w-3/4 max-w-4xl bg-white p-4 rounded-xl border border-slate-200 backdrop-blur-md">
          <h2 className="text-2xl font-semibold text-left mb-4">
            Edit Peserta JOTR
          </h2>
          <form id="form-edit-peserta" onSubmit={handleSubmit}>
            <InputFieldParticipants
              formData={formData}
              handleChange={handleChange}
              isFormDisabled={loading}
              labelColor="text-slate-800"
              eventId={eventId}
              categoryName={formData.kategori}
              categories={categories}
            />
          </form>
        </div>

        <div className="w-full md:w-1/4 max-w-sm bg-white p-4 rounded-xl border border-slate-200 backdrop-blur-md flex flex-col gap-4">
          <h2 className="text-2xl font-semibold text-left">
            Informasi Pesanan
          </h2>
          <div className="text-lg text-left">
            <p>
              Kategori: <strong>{formData.kategori || "N/A"}</strong>
            </p>
            <p>
              Order ID: <strong>{formData.orderId || "N/A"}</strong>
            </p>
            <p>
              Status Transaksi:{" "}
              <strong>{formData.transaction_status || "N/A"}</strong>
            </p>
            <p>
              Harga:{" "}
              <strong>Rp {formData.harga.toLocaleString() || "0"}</strong>
            </p>
            <p>
              Jarak:{" "}
              <strong>
                {formData.distance ? `${formData.distance} KM` : "N/A"}
              </strong>
            </p>
          </div>
          <div className="flex flex-col gap-4">
            <button
              type="submit"
              form="form-edit-peserta"
              className={`w-full py-3 bg-red-600 hover:bg-lime-700 transition-all duration-300 text-white font-semibold rounded-xl ${
                loading ? "cursor-not-allowed opacity-50" : ""
              }`}
              disabled={loading}
            >
              {loading ? "Menyimpan..." : "Simpan"}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className={`w-full py-3 bg-gray-300 text-gray-800 rounded-xl hover:bg-gray-400 transition-colors duration-200 font-semibold ${
                loading ? "cursor-not-allowed opacity-50" : ""
              }`}
              disabled={loading}
            >
              Batal
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
